import {
  IconButton,
  Icons,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { useWorkloadAutoscalerConfigSubdrawer } from '@cast/split-me/features/cluster/workload-optimization';
import type { PreparedWorkload } from '@cast/split-me/features/workload-optimization';

const WORKLOAD_AUTOSCALER_KEY = 'workloads.cast.ai/configuration';

const annotationBoxSx = {
  bgcolor: tokens.primitives.colors.grey['100'],
  border: `1px solid ${tokens.theme.border.structural.neutral}`,
  borderRadius: '2px',
  px: '2px',
  pb: '2px',
  maxWidth: '100%',
  wordBreak: 'break-word' as const,
};

const annotationTextSx = {
  fontFamily: 'Space Mono, monospace',
  fontSize: '12px',
  lineHeight: '18px',
  fontWeight: 400,
  color: tokens.primitives.colors.grey['600'],
};

/** Annotations section for inventory Meta tab — Figma inline-code style. */
export const MetaAnnotations = ({
  workload,
}: {
  workload: PreparedWorkload;
}) => {
  const { openWorkloadAutoscalerConfigSubdrawer } =
    useWorkloadAutoscalerConfigSubdrawer();

  const annotations = (workload.annotations ?? []).map(({ key, value }) => ({
    key,
    value,
    display: `${key}=${value}`,
    isAutoscalerConfig: key === WORKLOAD_AUTOSCALER_KEY,
  }));

  if (!annotations.length) {
    return (
      <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
        —
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      gap={8}
      flexWrap="wrap"
      alignItems="flex-start"
      data-testid="meta-annotations"
    >
      {annotations.map((annotation) => (
        <Box
          key={annotation.key}
          sx={{
            ...annotationBoxSx,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            width: annotation.display.length > 80 ? '100%' : 'auto',
          }}
        >
          <Typography component="span" sx={annotationTextSx}>
            {annotation.display}
          </Typography>
          {annotation.isAutoscalerConfig && (
            <Tooltip title="View full annotation" placement="bottom">
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="View full annotation"
                testId="open-workload-autoscaler-config"
                onClick={() =>
                  openWorkloadAutoscalerConfigSubdrawer({
                    config: {
                      key: annotation.key,
                      value: annotation.value,
                    },
                  })
                }
              >
                <Icons.ArrowSquareOut
                  color={tokens.primitives.colors.grey['600']}
                  fontSize={12}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
    </Stack>
  );
};
